import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    direction = "direction",
    alignment = "alignment",
    image = "image",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    customCSS = "customCSS",
    salesAvailableText = "salesAvailableText",
}

interface LinkModelExtended {
    fields: {
        openInNewWindow: boolean;
        overriddenTitle: string;
        destination: LinkFieldValue;
        image: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.image]: string;
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModelExtended[];
        [fields.customCSS]: string;
        [fields.salesAvailableText]: string;
    };
    extendedStyles?: LinkListStyles;
}

export interface LinkListStyles {
    image?: LazyImageProps;
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    linkListStyle?: InjectableCss;
    salesTitleWrapper?: InjectableCss;
}

export const linkListStyles: LinkListStyles = {
    linkListWrapper: {
        css: css`
            text-transform: uppercase;
            padding: 10px 0;
        `,
    },
    titleWrapper: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            margin-bottom: 10px;
            span {
                color: ${getProp("theme.typography.p.color")} !important;
            }
        `,
    },
    linkListStyle: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: 24px;
            text-transform: capitalize;
            span {
                color: ${COLORS.text.lighter} !important;
            }
        `,
    },
    linkWrapper: {
        css: css`
            padding: 1px 18px 0 0;
        `,
    },

    image: {
        css: css`
            padding-right: 10px;
        `,
    },
    salesTitleWrapper: {
        css: css`
            p {
                font-size: ${TYPOGRAPHY.size.tiny};
                line-height: 16px;
                font-style: italic;
                margin-top: 10px;
            }
        `,
    },
};

const defaultCustomCss = `.list-wrapper {
}
`;

export const LinkList: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { links } = useGetLinks(fields.links, o => o.fields.destination);
    const titleLink = useGetLink(fields.titleLink);
    const styles = useMergeStyles("linkList", linkListStyles, extendedStyles);

    const alignmentStyles = {
        css: css`
            text-align: ${fields.alignment || "right"};
            ${styles.linkListWrapper?.css}
        `,
    };

    const directionStyles = {
        css: css`
            ${fields.direction === "horizontal" ? "display: inline-block;" : "padding: 1px 0 0 0;"};
            ${styles.linkWrapper?.css}
            margin-bottom: 5px;
        `,
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="list-wrapper" {...alignmentStyles}>
                <StyledWrapper className="title-wrapper" {...styles.titleWrapper}>
                    {titleLink ? (
                        <Link className="list-title" href={titleLink.url}>
                            {fields.title || titleLink.title}
                        </Link>
                    ) : (
                        <Typography className="list-title" as="h3" {...styles.title}>
                            {fields.title}
                        </Typography>
                    )}
                </StyledWrapper>
                {links.map((list, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <StyledWrapper className="links-wrapper" key={index} {...directionStyles}>
                        <LazyImage src={fields.links[index].fields.image} {...styles.image} altText={fields.links[index].fields.overriddenTitle || list.title} />
                        {list.url && (
                            <Link
                                className="list-link"
                                href={list.url}
                                target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                {...styles.linkListStyle}
                            >
                                {fields.links[index].fields.overriddenTitle || list.title}
                            </Link>
                        )}
                    </StyledWrapper>
                ))}
                {fields.salesAvailableText && (
                    <StyledWrapper {...styles.salesTitleWrapper}>
                        <Typography as="p">{fields.salesAvailableText}</Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: LinkList,
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.salesAvailableText,
                displayName: "Sales Available Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    const result = cssLinter.parse(value, { silent: true });

                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
